import { DEFAULT_STATUS_PAGE_SETTINGS } from './status.js';
import { DEFAULT_FEEDBACK_STATES } from './feedback.js';
/**
 * OpenAI reasoning-effort ladder, lowest → highest, as of the gpt-5.6 family.
 * Older families accept only a subset (o-series: low…high, gpt-5.2–5.5 top out
 * at xhigh, …) — the API clamps a configured level to the nearest one the
 * target model supports (see apps/api/src/ai/openai.ts).
 */
export const REASONING_EFFORTS = ['none', 'low', 'medium', 'high', 'xhigh', 'max'];
/** Picker options for the OpenAI reasoning-effort selects, lowest → highest. */
export const REASONING_EFFORT_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'xhigh', label: 'X-High' },
    { value: 'max', label: 'Max' },
];
/** Keys of AppSettings whose value is an AiFeatureConfig — the runtime list
 *  lets the Settings "apply to all" control target every AI feature at once
 *  (`aiEmbeddings` is deliberately not an AiFeatureConfig, so it stays out). */
export const AI_FEATURE_KEYS = [
    'aiAnalysis',
    'aiReply',
    'aiTags',
    'aiWorkItem',
    'aiNextSteps',
    'aiPlaybook',
    'aiAuthoring',
    'aiTicketReply',
    'aiTicketComment',
    'aiTicketReview',
    'aiTicketAnalysis',
    'aiTicketNextSteps',
    'aiEntitlements',
    'aiDocVision',
    'aiSupportChat',
    'aiGuidesChat',
    'aiReleasePreparation',
    'aiDashboardLayout',
    'aiBacklogHistory',
    'aiCommsTriage',
    'aiCommsReply',
];
/** The audiences a piece of authored knowledge (article / document) can carry. */
export const KNOWLEDGE_AUDIENCES = ['internal', 'customer', 'end_user'];
/** Runtime guard for the SET vocabulary (articles/documents). 'both' is a legacy
 *  single-value-only value and is intentionally NOT accepted here. */
export function isKnowledgeAudience(x) {
    return x === 'internal' || x === 'customer' || x === 'end_user';
}
const DEFAULT_AI_FEATURE = {
    provider: 'openai',
    model: 'gpt-5.4-mini',
    think: 'medium',
    numCtx: 8192,
};
/**
 * Canonical defaults. Both the API (to fill gaps when reading the settings
 * table) and the web Settings page (to seed the form) merge stored values
 * over these so every field always has a concrete value.
 */
export const DEFAULT_APP_SETTINGS = {
    openaiApiKey: '',
    openaiModel: 'gpt-5.4-mini',
    ollamaBaseUrl: 'http://localhost:11434',
    ollamaModel: 'llama3.1',
    syncIntervalMinutes: 15,
    theme: 'dark',
    aiWorkingAnimation: 'constellation',
    thinkingAnimation: 'wizard',
    notificationsEnabled: false,
    aiAnalysis: { ...DEFAULT_AI_FEATURE },
    aiReply: { ...DEFAULT_AI_FEATURE },
    aiTags: { ...DEFAULT_AI_FEATURE },
    aiWorkItem: { ...DEFAULT_AI_FEATURE },
    aiNextSteps: { ...DEFAULT_AI_FEATURE },
    aiPlaybook: { ...DEFAULT_AI_FEATURE },
    aiAuthoring: { ...DEFAULT_AI_FEATURE },
    aiTicketReply: { ...DEFAULT_AI_FEATURE },
    aiTicketComment: { ...DEFAULT_AI_FEATURE },
    aiTicketReview: { ...DEFAULT_AI_FEATURE },
    aiTicketAnalysis: { ...DEFAULT_AI_FEATURE },
    aiTicketNextSteps: { ...DEFAULT_AI_FEATURE },
    aiEntitlements: { ...DEFAULT_AI_FEATURE },
    aiDocVision: { ...DEFAULT_AI_FEATURE },
    aiSupportChat: { ...DEFAULT_AI_FEATURE },
    aiGuidesChat: { ...DEFAULT_AI_FEATURE },
    aiReleasePreparation: { ...DEFAULT_AI_FEATURE },
    aiDashboardLayout: { ...DEFAULT_AI_FEATURE },
    aiBacklogHistory: { ...DEFAULT_AI_FEATURE },
    aiCommsTriage: { ...DEFAULT_AI_FEATURE },
    aiCommsReply: { ...DEFAULT_AI_FEATURE },
    aiEmbeddings: { provider: 'openai', model: 'text-embedding-3-small' },
    aiPause: { embeddings: false, chatVision: false },
    learning: {
        enabled: false,
        ragEnabled: true,
        ragTopK: 4,
        minSimilarity: 0.75,
        playbookEnabled: true,
        playbookAutoUpdate: false,
        playbookUpdateEveryNActions: 20,
    },
    discourse: {
        // Must equal the category hardcoded in the sync today, so behavior is
        // unchanged out of the box and the Settings picker pre-selects it.
        category: 'hypercare',
    },
    discourseActions: {
        replyRequireReview: true,
        autoSuggestTagsOnOpen: false,
        completionMode: 'accept-solution',
        productManagers: ['mortenwieth', 'Niclas_Andre_sorense'],
    },
    azure: {
        defaultAreaPath: '',
        defaultIterationPath: '',
        featuresTeam: '',
        assigneesByGroup: { feature: [], backlog: [] },
        severityToType: {
            critical: 'ado_bug',
            high: 'ado_bug',
            normal: 'ado_userstory',
            low: 'ado_userstory',
            wish: 'ado_feature',
        },
        severityToPriority: {
            critical: 1,
            high: 1,
            normal: 2,
            low: 3,
            wish: 4,
        },
        stateByType: {
            ado_bug: '',
            ado_userstory: '',
            ado_feature: '',
        },
        areaPathByType: {
            ado_bug: 'vNext',
            ado_userstory: 'vNext',
            ado_feature: 'vNext\\Team PO',
        },
        autoLinkBackToDiscourse: true,
        postWorkItemLinkAsReply: false,
        dedupEnabled: true,
        showNotOnBoardLane: false,
        allowGlobalConnectionFallback: false,
    },
    proofing: {
        ticketReply: true,
        ticketComment: true,
        ticketBug: true,
        topicReply: true,
        topicWorkItem: true,
        backlogItem: true,
    },
    nextSteps: {
        enabled: true,
        autoComputeOnOpen: true,
    },
    autosync: {
        // All off by default — the user opts in per integration from Settings.
        discourse: { enabled: false, dayIntervalMinutes: 30, nightIntervalMinutes: 180 },
        azureDevops: { enabled: false, dayIntervalMinutes: 30, nightIntervalMinutes: 180 },
        commits: { enabled: false, dayIntervalMinutes: 30, nightIntervalMinutes: 180 },
        zoho: { enabled: false, dayIntervalMinutes: 15, nightIntervalMinutes: 120 },
        // Companies (Zoho CRM, F3): standard 15-min cadence. On by default — actual
        // firing is gated by isConfigured (ZOHO_CRM_REFRESH_TOKEN), so it stays
        // dormant until CRM credentials are provided.
        zohoCrm: { enabled: true, dayIntervalMinutes: 15, nightIntervalMinutes: 60 },
        // Messages (Microsoft 365, F4): standard cadence. On by default — actual
        // firing is gated by isConfigured (MS_GRAPH_CONNECT_ENABLED), so it stays
        // dormant until the Microsoft connection feature is enabled.
        msComms: { enabled: true, dayIntervalMinutes: 10, nightIntervalMinutes: 30 },
        // Comms triage (F4.2): standard cadence just behind ms-comms so the mirror is
        // populated first. On by default — actual firing is gated by isConfigured
        // (MS_GRAPH_CONNECT_ENABLED), so it stays dormant until MS is enabled.
        commsTriage: { enabled: true, dayIntervalMinutes: 12, nightIntervalMinutes: 35 },
        repos: { enabled: false, dayIntervalMinutes: 30, nightIntervalMinutes: 180 },
        realequity: { enabled: false, dayIntervalMinutes: 15, nightIntervalMinutes: 60 },
    },
    knowledge: {
        enabled: false,
        sources: { workItems: true, discourse: true, zoho: false, documents: true, commits: true, cocoConfig: false },
        autoEmbedCocoConfig: false,
        autoVariantScan: false,
        autoLinkCocoConfig: false,
        zohoImageExtraction: true,
        entityImageExtraction: true,
        attachmentTextExtraction: true,
        attachmentPredownload: true,
        autoEmbed: { enabled: true, intervalMinutes: 5, batchPerRound: 50 },
        topK: 6,
        minSimilarity: 0.72,
        indexLookbackMonths: 0,
        visionMaxAgeMonths: 12,
        maxItemAgeMonths: 0,
        recencyBias: 0.3,
        preferCompletedStates: true,
    },
    authoring: {
        enabled: false,
        language: 'en',
        maxStoriesPerFeature: 6,
        // 3 boards is typically ALL of them (a linked Feature carries a handful at
        // most), and 6 fresh readings covers a cold Feature's screenshots in one go.
        // Both must exist HERE and not only on the interface: mergeDefaults walks the
        // keys of this object, so a field declared but not defaulted reads as
        // undefined on the server forever.
        feedbackBoardsPerDraft: 3,
        feedbackBoardVisionImages: 6,
    },
    zoho: {
        // Support + Dispatch department ids (CB org, EU).
        departmentIds: ['193116000000007061', '193116000010077849'],
        syncLookbackMonths: 1,
        allowPublicReply: false,
        replyRequireReview: true,
        fromEmailByDepartment: {},
        replyFromAddress: '',
        replySignature: '',
    },
    releaseTracking: {
        enabled: true,
        idleGapMinutes: 10,
        maxWindowMinutes: 60,
        notifyOnDetect: true,
    },
    releaseNotes: {
        autoDraft: true,
    },
    entitlements: {
        enabled: true,
        autoGenerate: false,
        // Common ADO states that mean work is real. Editable (states are custom).
        sourceStates: ['Active', 'Resolved', 'Closed', 'Done', 'Committed'],
        dedupMinSimilarity: 0.82,
        passLimit: 40,
    },
    employeeProfiles: {
        enabled: true,
        lookbackMonths: 12,
    },
    support: {
        enabled: true,
        agentName: 'Robin',
        agentTitle: 'RealEquity Support',
        welcomeMessage: '',
        extraPlaybook: '',
        extraExamples: '',
        startLanguage: 'auto',
        tone: 'warm',
        offTopicPolicy: 'strict',
        maxInputChars: 4000,
        knowledgeUseDocuments: true,
        knowledgeDocumentScope: 'user',
        knowledgeUseArticles: true,
        knowledgeUseAppNavigation: true,
        knowledgeUseCocoConfig: false,
        knowledgeUseFeatures: false,
        knowledgeTopK: 6,
        knowledgeMinSimilarity: 0,
        verificationEnabled: false,
        verifyOnlyWhenUncertain: true,
        autoAnswerMinConfidence: 0.45,
        notifyOnEscalation: true,
    },
    // SAFETY DEFAULT: auto-journalization OFF. Triaged customer comms stay in the
    // private per-user 'pending' staging until an operator turns this on.
    comms: {
        autoJournalize: false,
    },
    statusPage: DEFAULT_STATUS_PAGE_SETTINGS,
    userAdmin: {
        allowedEmails: [],
    },
    branding: {
        companyName: '',
        logoUrl: '',
        accentColor: '',
        pdfFooterText: '',
        pdfPageSize: 'A4',
        pdfOrientation: 'portrait',
        pdfMargins: { top: 15, right: 15, bottom: 15, left: 15 },
    },
    boardLanes: {
        features: [],
        storiesBugs: [],
    },
    // Cloned, so a caller that mutates the resolved settings can never corrupt the
    // canonical seed the Settings editor resets to.
    feedbackStates: DEFAULT_FEEDBACK_STATES.map((s) => ({ ...s })),
};
/**
 * The keys a person can actually edit — exactly the shape of `AppSettings`.
 *
 * The `settings` TABLE is wider than this type: it is also where machine-written
 * operational state lives, including every integration's incremental sync cursor
 * (`advanceCursorMs` in api/services/sync-cursor.ts), cached upstream vocabularies
 * (`discourse:categories`, `zoho:departments`, the Azure state cache) and AI
 * playbooks. None of that is a setting anybody edits, and none of it is part of
 * the `AppSettings` contract the client is typed against.
 *
 * Derived from DEFAULT_APP_SETTINGS rather than listed by hand, so the allow-list
 * cannot drift from the type it is protecting. (b-01993)
 */
export const APP_SETTINGS_KEYS = Object.keys(DEFAULT_APP_SETTINGS);
/**
 * Project a raw settings blob onto the editable surface.
 *
 * Used on BOTH ends of the wire, from this one definition: the API sends only
 * these keys to a client, and the Settings page is written against the same set.
 * Shipping machine state to the browser is what let a sync advancing a cursor
 * read as an unsaved change the user had made — and let Save write the stale
 * cursor back over it. (b-01993)
 *
 * Keys ABSENT from `raw` stay absent rather than being filled with a default:
 * callers deep-merge their own defaults, and the difference between "unset" and
 * "set to the default" is what a diff against the stored value depends on.
 */
export function pickAppSettings(raw) {
    const out = {};
    for (const key of APP_SETTINGS_KEYS)
        if (key in raw)
            out[key] = raw[key];
    return out;
}
/**
 * Derive the display status. Discourse topic state (archived/closed) takes
 * precedence over the Solved-plugin's accepted-answer flag; an open topic is
 * "solved" only when it has an accepted answer.
 */
export function computePcpState(t) {
    if (t.archived)
        return 'archived';
    if (t.closed)
        return 'closed';
    if (t.solved)
        return 'solved';
    return 'open';
}
/**
 * GitHub-style heading → anchor slug. MUST be identical on the backend chunker
 * (which stores each chunk's `anchor`) and the reader/PDF (which set heading
 * `id=`), so section-level citation deep-links (`/knowledge/articles/{slug}#{anchor}`)
 * land. Unicode-aware so non-ASCII headings (e.g. Danish æøå) stay stable.
 */
export function slugifyHeading(text) {
    return String(text || '')
        .trim()
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s-]/gu, '') // drop punctuation, keep unicode letters/numbers
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
}
// ─── Solution Design (the page-specification area, f-00127) ──────
// Page specifications are knowledge_articles rows with docType='page-spec'. They
// live in their own global area at /solution-design, arranged in a folder tree
// that mirrors RealEquity's menu, and carry tags for cross-cutting retrieval.
/** How deep the folder tree may nest. Mirrors STATUS_COMPONENT_MAX_DEPTH — the
 *  depth of a node is DERIVED from its ancestors on read, never stored. */
export const SPEC_FOLDER_MAX_DEPTH = 5;
/** Hard cap on tags per specification. A per-item budget is the best-evidenced
 *  defence against tag rot: scarcity forces discriminating terms instead of a
 *  long tail of one-use tags (Santos, Burghardt, Lerman & Helic, arXiv:2104.01028;
 *  Discourse ships the same control as max_tags_per_topic). The NUMBER is a
 *  judgement call and a candidate for a setting; the existence of a cap is not. */
export const SPEC_MAX_TAGS = 5;
/** Longest a single tag may be, after normalisation. */
export const SPEC_TAG_MAX_LENGTH = 40;
export const ENTITLEMENT_EXPOSURES = ['internal', 'private_preview', 'beta', 'ga'];
export const ENTITLEMENT_STAGES = [
    'planned', 'in_development', 'shipped_internal', 'staged', 'released', 'deprecated',
];
export const ENTITLEMENT_NOTE_VISIBILITIES = [
    'customer_facing', 'internal_only', 'hidden',
];
export const ENTITLEMENT_GROUP_LEVELS = ['application', 'domain', 'capability'];
export const ENTITLEMENT_GENERATION_LENSES = [
    'built', 'demand', 'gap', 'guided', 'paste', 'organize', 'commits', 'backlog',
];
export const RELEASE_TRAIN_STATES = ['planning', 'frozen', 'released', 'archived'];
/**
 * Workflow/check conclusions that count as a real, blocking CI failure (red ✗).
 * GitHub's own green-check rollup does NOT go red for `cancelled` (concurrency
 * `cancel-in-progress`, manual cancels), `skipped`, or `neutral` runs — counting
 * those produced false reds on commits GitHub shows as green. `stale` was never a
 * real workflow-run conclusion either. Single source of truth for the API rollups
 * (getCommitCiRollup / getCommitStatus) and the web per-run icons, so the list can't
 * drift across the three copies that previously diverged.
 */
export const CI_FAILURE_CONCLUSIONS = [
    'failure',
    'timed_out',
    'startup_failure',
    'action_required',
];
/** Backlog board card density (per-user preference). Increasing detail; default 'standard'. */
export const BACKLOG_CARD_LAYOUTS = ['minimal', 'compact', 'standard', 'detailed', 'extensive'];
export const DEFAULT_BACKLOG_CARD_LAYOUT = 'standard';
/** Backlog board card TYPE (per-user preference) — an axis orthogonal to density:
 *  'wide' is the default roomy card; 'narrow' is a compact card in ~40%-slimmer lanes.
 *  Both types honour all five density tiers above. Default 'wide'. */
export const BACKLOG_CARD_TYPES = ['wide', 'narrow'];
export const DEFAULT_BACKLOG_CARD_TYPE = 'wide';
/** Fibonacci tier used for small story-point estimates. */
export const STORY_POINT_FIB = [1, 2, 3, 5, 8, 13];
/**
 * Snap an arbitrary number to a valid story-point value:
 * Fibonacci (1/2/3/5/8/13) for small estimates, and multiples of 5 (≥20) above —
 * i.e. "Fibonacci up to ~20, then anything ending in 0 or 5". Returns 0 for none.
 */
export function snapStoryPoints(n) {
    if (n == null || !Number.isFinite(n) || n <= 0)
        return 0;
    // Small estimates snap to the nearest Fibonacci value; the ~16 cutoff is the
    // midpoint between 13 and 20 so 17+ rounds up into the multiples-of-5 tier.
    if (n <= 16) {
        return STORY_POINT_FIB.reduce((best, v) => (Math.abs(v - n) < Math.abs(best - n) ? v : best), STORY_POINT_FIB[0]);
    }
    return Math.max(20, Math.round(n / 5) * 5);
}
/** Build the selectable story-point options up to `max` (Fibonacci then step-5). */
export function storyPointOptions(max = 100) {
    const opts = [...STORY_POINT_FIB];
    for (let v = 20; v <= max; v += 5)
        opts.push(v);
    return opts;
}
// ─── Feature / Bug Requests (PCP's own kind) ─────────────────────
export const FEATURE_REQUEST_TYPES = ['feature', 'bug'];
/** Lifecycle status (PCP-managed; never written back to Discourse). Two-level:
 *  the substate groups into an Open / Closed STATE (see FEATURE_REQUEST_STATUS_STATE). */
// 'planning' = an agent/owner has CLAIMED the request and is actively planning it
// (so it leaves 'new' and won't be picked up twice); precedes 'planned' (a plan exists,
// not yet being implemented) → 'in_progress'.
export const FEATURE_REQUEST_STATUSES = ['new', 'planning', 'planned', 'in_progress', 'implemented', 'rejected'];
export const FEATURE_REQUEST_STATES = ['open', 'closed'];
/** Open{New, Planning, Planned, In progress} / Closed{Implemented, Rejected}. */
export const FEATURE_REQUEST_STATUS_STATE = {
    new: 'open',
    planning: 'open',
    planned: 'open',
    in_progress: 'open',
    implemented: 'closed',
    rejected: 'closed',
};
export const FEATURE_REQUEST_STATE_STATUSES = {
    open: ['new', 'planning', 'planned', 'in_progress'],
    closed: ['implemented', 'rejected'],
};
export const FEATURE_REQUEST_STATUS_LABELS = {
    new: 'New',
    planning: 'Planning',
    planned: 'Planned',
    in_progress: 'In progress',
    implemented: 'Implemented',
    rejected: 'Rejected',
};
export const FEATURE_REQUEST_STATE_LABELS = { open: 'Open', closed: 'Closed' };
export const statusToState = (s) => FEATURE_REQUEST_STATUS_STATE[s] ?? 'open';
/** Statuses at which a request's AUTHOR may still edit/delete their OWN request.
 *  Editing is allowed while New, Planning or Planned; once the request reaches In
 *  progress (or any Closed status) the content is frozen to the author — changing it
 *  then requires the requests.manage permission. */
export const FEATURE_REQUEST_AUTHOR_EDITABLE_STATUSES = ['new', 'planning', 'planned'];
export const isAuthorEditableStatus = (s) => FEATURE_REQUEST_AUTHOR_EDITABLE_STATUSES.includes(s);
export const isFeatureRequestStatus = (s) => FEATURE_REQUEST_STATUSES.includes(s);
/** Coerce a raw/legacy persisted status to a canonical one (unknown → 'new',
 *  matching the migration that renamed the old flat 'open'). Use wherever a stored
 *  status drives logic so the DTO (mapRow) and the enforcement gates never disagree
 *  on a non-canonical value. */
export const coerceFeatureRequestStatus = (s) => isFeatureRequestStatus(s) ? s : 'new';
/** PCP product areas a request can belong to (AI-classified on create, editable
 *  afterward; 'General' is the default/fallback). Drives the release-notes grouping. */
export const FEATURE_REQUEST_AREAS = [
    'Dashboard', 'Tickets', 'Topics', 'Backlog', 'Commit', 'Release', 'Status Page',
    'Insights', 'Knowledge Base', 'Smartguides', 'MCP Server', 'Global Settings', 'User Settings', 'General',
];
export const FEATURE_REQUEST_AREA_EMOJI = {
    Dashboard: '📊',
    Tickets: '🎫',
    Topics: '💬',
    Backlog: '📋',
    Commit: '🔧',
    Release: '🚀',
    'Status Page': '🚦',
    Insights: '📈',
    'Knowledge Base': '📚',
    Smartguides: '🧭',
    'MCP Server': '🔌',
    'Global Settings': '⚙️',
    'User Settings': '👤',
    General: '🧩',
};
/** Per-area description + keywords used to build the AI area-classification prompt. */
export const FEATURE_REQUEST_AREA_INFO = {
    Dashboard: {
        description: 'PCP home/overview landing (route /): cross-system KPIs, charts, recent-activity widgets, at-a-glance stats.',
        keywords: ['dashboard', 'overview', 'home', 'landing', 'kpi', 'stats', 'chart', 'widget', 'summary'],
    },
    Tickets: {
        description: 'Zoho Desk customer SUPPORT TICKETS (route /tickets), managed by C&B Support — replies, internal notes, ticket status/department/SLA.',
        keywords: ['ticket', 'zoho', 'zoho desk', 'support', 'customer', 'agent', 'reply', 'internal note', 'department', 'sla', 'desk'],
    },
    Topics: {
        description: 'Discourse COMMUNITY/FORUM threads (route /topics), managed by C&B Dispatch — posts, replies, categories, mark-solved.',
        keywords: ['topic', 'discourse', 'forum', 'community', 'thread', 'post', 'reply', 'solved', 'category', 'dispatch'],
    },
    Backlog: {
        description: 'Azure DevOps WORK ITEMS (route /backlog): Features, User Stories, Bugs; board/kanban, states, assignees, AB# refs.',
        keywords: ['backlog', 'azure devops', 'ado', 'work item', 'feature', 'user story', 'bug', 'board', 'kanban', 'sprint', 'state', 'assignee', 'ab#'],
    },
    Commit: {
        description: 'COMMIT TRACKER (route /commits): git commits, repos, PRs, linking commits to work items.',
        keywords: ['commit', 'git', 'repository', 'repo', 'pr', 'pull request', 'diff', 'branch', 'commit message', 'code change'],
    },
    Release: {
        description: 'RELEASE PLANNING (route /releases): release notes, release trains, deployments, versions, changelog, entitlement rollout.',
        keywords: ['release', 'release notes', 'changelog', 'deploy', 'deployment', 'version', 'train', 'rollout', 'entitlement', 'publish'],
    },
    'Status Page': {
        description: "PUBLIC STATUS PAGE (route /status, plus the public site on the status.* hostnames): component tree and their health, incidents & maintenance windows with their updates, audiences/customer groups, the 90-day uptime history, and the subscriber side — signup, double opt-in confirmation, manage/unsubscribe, and the email/SMS notifications sent when something breaks.",
        keywords: ['status page', 'statuspage', 'status', 'driftsstatus', 'incident', 'maintenance', 'outage', 'downtime', 'uptime', 'component', 'audience', 'subscriber', 'subscribe', 'tilmeld', 'unsubscribe', 'notification', 'statuscast', 'postmark', 'gatewayapi'],
    },
    Insights: {
        description: 'INSIGHTS / analytics (route /insights): dedicated cross-system reports, metrics, trends and AI-generated analysis — deeper than the home Dashboard overview.',
        keywords: ['insights', 'analytics', 'report', 'metric', 'trend', 'analysis', 'statistics', 'data', 'breakdown', 'visualization'],
    },
    'Knowledge Base': {
        description: 'KNOWLEDGE BASE (route /knowledge): help articles, documentation, FAQs and how-to content surfaced to users. NOT the in-app Smartguides tours.',
        keywords: ['knowledge base', 'kb', 'documentation', 'docs', 'article', 'help article', 'faq', 'how-to', 'knowledge'],
    },
    Smartguides: {
        description: "SMARTGUIDES (route /guides): PCP's self-hosted in-app digital adoption guides — walkthroughs, tooltips, launchers, the guide designer/recorder and the embedded player (the WalkMe replacement).",
        keywords: ['smartguide', 'smartguides', 'guide', 'walkthrough', 'tooltip', 'launcher', 'designer', 'recorder', 'player', 'dap', 'walkme', 'onboarding tour', 'step'],
    },
    'MCP Server': {
        description: 'MCP SERVER: the Model Context Protocol server that exposes PCP data and tools (tickets, topics, backlog, feature/bug requests) to AI agents — its tools, scopes, auth and resources.',
        keywords: ['mcp', 'mcp server', 'model context protocol', 'tool', 'agent', 'scope', 'mcp:write', 'resource', 'api key', 'integration tool'],
    },
    'Global Settings': {
        description: 'GLOBAL / ORG-WIDE SETTINGS (route /settings, gated by settings.manage): AI providers/models, integration credentials, sync schedules, entitlements, roles/permissions — configuration that affects the whole org/app for everyone.',
        keywords: ['settings', 'global settings', 'admin', 'configuration', 'integration credentials', 'provider', 'model', 'sync schedule', 'roles', 'permissions', 'rbac', 'org-wide'],
    },
    'User Settings': {
        description: "USER / PERSONAL SETTINGS — the Profile page: the signed-in user's own API keys, connected accounts, appearance/theme, notification & review preferences — anything keyed to the individual user.",
        keywords: ['profile', 'user settings', 'personal', 'my account', 'connected account', 'api key', 'appearance', 'theme', 'notification preference', 'preferences'],
    },
    General: {
        description: 'Cross-cutting / anything not in a specific area above: auth/login, global navigation/sidebar, search, the AI assistant/chat, app-wide chrome, or otherwise uncategorised. The DEFAULT/fallback.',
        keywords: ['general', 'login', 'auth', 'sign in', 'navigation', 'sidebar', 'search', 'ai assistant', 'chat', 'app-wide', 'cross-cutting', 'uncategorised', 'misc'],
    },
};
/** Human-facing reference id for a request, e.g. f-00123 (feature) / b-00007 (bug). */
export function formatRequestRefId(type, number) {
    const l = type === 'bug' ? 'b' : 'f';
    return `${l}-${String(number).padStart(5, '0')}`;
}
/** Human-facing short id for a KB document (article or page-spec), e.g. kb-42.
 *  Lowercase, no zero-padding; unique across all KB docs. Shown in the page header
 *  and used as the stable internal handle for cross-page references. */
export function kbShortId(number) {
    return `kb-${number}`;
}
// ─── Release Notes (AI-drafted, human-published; bundles shipped items) ──
// A release-notes entry groups shipped feature_requests (status='implemented',
// release_note_id IS NULL = unreleased) into one "what's new" announcement.
// DISTINCT from the deployment/customer-release domains under /api/releases/*.
export const RELEASE_NOTE_STATUSES = ['draft', 'published'];
export const RELEASE_NOTE_STATUS_LABELS = { draft: 'Draft', published: 'Published' };
/**
 * The tag vocabulary as `{tag, count}`, whichever shape the API answered with.
 * Prefers `tagCounts`; falls back to `tags`, mapping bare strings to `count: 0`.
 * Order is preserved (the server sends most-used first).
 */
export function tagFacetsOf(source) {
    if (!source)
        return [];
    const counted = source.tagCounts;
    if (Array.isArray(counted) && counted.length > 0) {
        return counted
            .filter((t) => !!t && typeof t.tag === 'string' && !!t.tag)
            .map((t) => ({ tag: t.tag, count: Number(t.count) || 0 }));
    }
    const raw = source.tags;
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const t of raw) {
        if (typeof t === 'string') {
            if (t)
                out.push({ tag: t, count: 0 });
        }
        else if (t && typeof t.tag === 'string' && t.tag)
            out.push({ tag: t.tag, count: Number(t.count) || 0 });
    }
    return out;
}
// ─── Deploy skew: is the running bundle still the deployed one? ──
//
// A browser keeps serving the bundle it has cached across a deploy, so a tab can
// be running last release's JavaScript against this release's API. When that
// combination crashes, the running CODE is not at fault — the BUILD is out of
// date, and one reload cures it for good. Distinguishing the two is the whole
// job: a crash on the current build is a real bug and must never trigger a
// reload (that would hide it behind a loop). See the error boundary's heal path
// and b-01797; b-01795 is the crash that motivated it.
//
// Extracted here so the rule is unit-tested rather than only reasoned about.
/**
 * True when `running` (the bundle's own build version) is provably NOT what the
 * server says is deployed — the one case where an automatic reload is warranted.
 *
 * Deliberately conservative: anything unknown answers false, so a missing or
 * unreadable server version, a dev build, or an equal pair all leave the crash
 * to be surfaced normally.
 */
export function isStaleClientBuild(running, deployed) {
    if (!running || !deployed)
        return false;
    // A local/unversioned build has no release identity to compare — never reload
    // (a dev crash must stay on screen, and vite's HMR owns reloading there).
    if (running === 'dev' || deployed === 'dev')
        return false;
    return running !== deployed;
}
/**
 * Is this path answered from the service worker's precache?
 *
 * Only the app shell is: `index.html` is the one HTML entry in the precache
 * manifest, and workbox maps a bare directory request onto it (`directoryIndex`).
 * Every other route — `/backlog/123`, `/tickets/…` — matches no route in the
 * worker (no `navigateFallback` is registered), so the navigation goes to the
 * network and a plain reload already lands on the fresh shell.
 *
 * That difference decides whether escaping a stale build needs to be
 * destructive, so it is stated here once rather than guessed at the call site.
 */
export function isPrecachedAppShellPath(pathname) {
    return pathname === '/' || pathname === '/index.html';
}
/** Hard recoveries to allow per deployed version before giving up on wiping. */
export const MAX_HARD_RECOVERIES = 2;
/**
 * True when the user should be offered the newly deployed build.
 *
 * Driven by the version comparison rather than by service-worker state, so the
 * toast appears exactly when the server really is serving something else — and
 * stays hidden when the running build is already current, however many stale
 * workers are sitting in `waiting`.
 *
 * Conservative by construction: `isStaleClientBuild` answers false for an
 * unknown/unreadable server version and for dev builds, so an offline tab, a
 * mid-deploy API and `pnpm dev` all stay quiet.
 */
export function shouldOfferUpdate(input) {
    // Offline the service worker answers /api/health from its own NetworkFirst
    // cache, so `deployed` can be an old release we have since moved PAST — which
    // compares as skew and would offer an "update" that cannot be fetched, and
    // whose recovery path would bin the caches keeping the app usable. Wait for
    // the network to have an opinion.
    if (!input.online)
        return false;
    if (input.deployInProgress)
        return false;
    if (!isStaleClientBuild(input.running, input.deployed))
        return false;
    // Sticky dismissal, keyed to the version dismissed: the NEXT release differs
    // from the stored one, so it prompts again — exactly once.
    if (input.dismissedForVersion && input.dismissedForVersion === input.deployed)
        return false;
    return true;
}
/**
 * Which mechanism to use for this update attempt.
 *
 * The polite handoff is tried once. If it has already been tried for this
 * deployed version and we are STILL running the old build, it demonstrably does
 * not work here (a wedged worker, or a precached `/` re-serving the old shell),
 * so escalate instead of repeating it. With no worker waiting there is nothing
 * to hand off to, and a plain reload would just re-serve the precached shell —
 * escalate immediately.
 */
export function updateApplyStrategy(input) {
    // The polite handoff gets exactly one turn per deployed version.
    if (input.workerWaiting && input.previousAttempts === 0)
        return 'skip-waiting';
    // Off the app shell the worker is not in the way at all: the navigation goes
    // to the network either way, so a reload is both sufficient and free. Never
    // bin the precache to solve a problem a reload already solves.
    if (!input.pathServedFromPrecache)
        return 'plain-reload';
    // Wiping the precache has now had its chances and the build is still behind,
    // so the cause is not the worker. Stop paying for the download and settle for
    // the harmless reload rather than destroying the offline shell again.
    if (input.previousAttempts > MAX_HARD_RECOVERIES)
        return 'plain-reload';
    return 'hard-recover';
}
//# sourceMappingURL=types.js.map